import { useState } from 'react';

import { Avatar, Button, Icon, Menu, Text } from '@zillow/constellation-10';
import {
  IconChevronDownOutline,
  IconChevronUpOutline,
  IconCreditCardFilled,
  IconDoorOpenFilled,
  IconExternalFilled,
  IconUserSettingsFilled,
} from '@zillow/constellation-icons';

import { CSTriggerSourceFromHeaderTitlesMap, HeaderTitles } from '../../constants';
import HeaderNavigationClickstreamEvents from '../../utils/clickstream';

import type { Paths } from '@zg-rentals/paths-manifest';
import type { Displays, User } from '../../types';

export type Props = {
  displays: Partial<Displays>;
  paths: Partial<Paths>;
  userModel?: User;
  avatarOnly?: boolean;
};

const AccountMenu = ({
  displays,
  paths,
  userModel = {
    features: {},
  } as User,
  avatarOnly = false,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);

  const {
    displayBilling,
    displayMFHResources,
    displayMainAccountSettings,
    displayMainBilling,
    displayMainHelp,
    displayMainResources,
    displayZillowAccountSettings,
  } = displays;

  const {
    accountSettingsPage = '',
    billingDue,
    billingPage,
    helpPage = '',
    homePage,
    mfhResourcesPage,
    postLogout = '',
    resourcesPage,
    userSettingsPage = '',
  } = paths;

  const { email = '', firstName = '', lastName = '', loggedIn, profileImageUrl = '' } = userModel;
  const fullName = `${firstName} ${lastName}`.trim();
  const isFirstOrLastNameEmptyOrNull = Boolean(!firstName) || Boolean(!lastName);
  const displayName = isFirstOrLastNameEmptyOrNull === false ? fullName : email;

  const handleLinkClick = (pageClickingTo: string) => {
    if (loggedIn && userModel) {
      const csLink = CSTriggerSourceFromHeaderTitlesMap[pageClickingTo];

      if (csLink) {
        HeaderNavigationClickstreamEvents.ClickNavigationLink({ user: userModel, link: csLink });
      }
    }
  };

  return (
    <Menu.Root onOpenChange={setIsOpen} dividers={true}>
      <Menu.Trigger data-testid="AccountMenu">
        <Button.Root
          css={{
            '&:not(:focus-visible), &[aria-expanded="true"]': { boxShadow: 'none' },
            color: 'text.subtle',
            ...(avatarOnly
              ? { backgroundColor: 'transparent', paddingBlock: 0, paddingInline: 0, borderRadius: '50%' }
              : {}),
          }}
        >
          <Button.Label css={{ width: '100%', display: 'flex', alignItems: 'center' }}>
            {profileImageUrl ? (
              <Avatar
                size={avatarOnly ? 'md' : 'sm'}
                css={{
                  marginRight: avatarOnly ? '0' : '200',
                  ...(avatarOnly ? { border: '1px solid {colors.border.muted}' } : {}),
                }}
              >
                <Avatar.Image alt={displayName} src={profileImageUrl} className="fs-exclude" />
              </Avatar>
            ) : (
              <Avatar
                fullName={isFirstOrLastNameEmptyOrNull === false ? fullName : undefined}
                size={avatarOnly ? 'md' : 'sm'}
                css={{
                  marginRight: avatarOnly ? '0' : '200',
                  ...(avatarOnly ? { border: '1px solid {colors.border.muted}' } : {}),
                }}
                tone="accent-2"
                className="fs-exclude"
              />
            )}
            {!avatarOnly && (
              <>
                <Text className="fs-mask" css={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                  {displayName}
                </Text>
                <Icon
                  render={isOpen ? <IconChevronUpOutline /> : <IconChevronDownOutline />}
                  size="sm"
                  css={{ color: 'icon.subtle', marginLeft: '100' }}
                />
              </>
            )}
          </Button.Label>
        </Button.Root>
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Content
          css={{
            '&:hover [data-c11n-component="Menu.Item"]:focus-visible [data-c11n-component="Menu.ItemLabel"]': {
              boxShadow: 'none',
            },
          }}
        >
          <Menu.Group>
            {displayMainAccountSettings && (
              <Menu.Item onClick={() => handleLinkClick(HeaderTitles.accountSettings)} asChild>
                <a href={accountSettingsPage}>
                  <Menu.ItemLabel>{HeaderTitles.accountSettings}</Menu.ItemLabel>
                  <Menu.ItemIcon render={<IconUserSettingsFilled />} />
                </a>
              </Menu.Item>
            )}
            {displayZillowAccountSettings && (
              <Menu.Item onClick={() => handleLinkClick(HeaderTitles.accountSettings)} asChild>
                <a href={userSettingsPage} target="_blank" rel="noreferrer">
                  <Menu.ItemLabel>{HeaderTitles.accountSettings}</Menu.ItemLabel>
                  <Menu.ItemIcon render={<IconExternalFilled />} />
                </a>
              </Menu.Item>
            )}
            {displayBilling && !displayMainBilling && (
              <Menu.Item onClick={() => handleLinkClick(HeaderTitles.billing)} asChild>
                <a href={`${billingPage}/${billingDue}`}>
                  <Menu.ItemLabel>{HeaderTitles.billing}</Menu.ItemLabel>
                  <Menu.ItemIcon render={<IconCreditCardFilled />} />
                </a>
              </Menu.Item>
            )}
          </Menu.Group>
          <Menu.Group>
            {Boolean(displayMFHResources || displayMainResources) && (
              <>
                {displayMFHResources ? (
                  <Menu.Item onClick={() => handleLinkClick(HeaderTitles.mfhResources)} asChild>
                    <a href={mfhResourcesPage} target="_blank" rel="noreferrer">
                      <Menu.ItemLabel>{HeaderTitles.mfhResources}</Menu.ItemLabel>
                      <Menu.ItemIcon render={<IconExternalFilled />} />
                    </a>
                  </Menu.Item>
                ) : (
                  <Menu.Item onClick={() => handleLinkClick(HeaderTitles.resources)} asChild>
                    <a href={resourcesPage} target="_blank" rel="noreferrer">
                      <Menu.ItemLabel>{HeaderTitles.resources}</Menu.ItemLabel>
                      <Menu.ItemIcon render={<IconExternalFilled />} />
                    </a>
                  </Menu.Item>
                )}
              </>
            )}
            {displayMainHelp && (
              <Menu.Item onClick={() => handleLinkClick(HeaderTitles.help)} asChild>
                <a href={helpPage} target="_blank" rel="noreferrer">
                  <Menu.ItemLabel>{HeaderTitles.help}</Menu.ItemLabel>
                  <Menu.ItemIcon render={<IconExternalFilled />} />
                </a>
              </Menu.Item>
            )}
          </Menu.Group>
          <Menu.Group>
            <Menu.Item onClick={() => handleLinkClick(HeaderTitles.backToBrand)} asChild>
              <a href={homePage} target="_blank" rel="noreferrer">
                <Menu.ItemLabel>{HeaderTitles.backToBrand}</Menu.ItemLabel>
                <Menu.ItemIcon render={<IconExternalFilled />} />
              </a>
            </Menu.Item>
            <Menu.Item onClick={() => handleLinkClick(HeaderTitles.signOut)} asChild aria-label="Sign out">
              <a href={postLogout}>
                <Menu.ItemLabel>{HeaderTitles.signOut}</Menu.ItemLabel>
                <Menu.ItemIcon render={<IconDoorOpenFilled />} />
              </a>
            </Menu.Item>
          </Menu.Group>
        </Menu.Content>
      </Menu.Portal>
    </Menu.Root>
  );
};

export default AccountMenu;
